import type {
  PatientProcedureCode,
  PatientProcedureCodePostingStatusEnum,
} from "../../../../../services";

// Display metadata for the sub-table's Status column, keyed by the backend's
// posting-status enum.
export const CPT_POSTING_STATUS_DISPLAY: Record<
  PatientProcedureCodePostingStatusEnum,
  { label: string; color: string }
> = {
  PAID: { label: "Paid", color: "#1E7A3C" },
  UNPAID: { label: "Unpaid", color: "#475467" },
  DENIED: { label: "Denied", color: "#B42318" },
};

// A money value is only real when the backend sent it. null/undefined means the
// payer has not reported it, and the column renders "-" rather than $ 0.00.
const num = (v: number | null | undefined): number | null =>
  v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v);

// Money summed in binary floating point lands on values like
// -1.1368683772161603e-13 instead of 0. Round totals to cents.
export const round2 = (n: number): number => Math.round(n * 100) / 100;

export type CptAmounts = {
  bill: number | null;
  insurancePaid: number | null;
  allowed: number | null;
  deduct: number | null;
  coIns: number | null;
  coPay: number | null;
  patientResp: number | null;
  contractAdj: number | null;
  otherAdj: number | null;
  balance: number | null;
};

/**
 * The single source of truth for a CPT line's amounts: every column maps to
 * exactly one backend key. Nothing is derived, summed from siblings, or
 * defaulted — a key the backend did not send renders as "-".
 *
 * Sub-table column      backend key
 * -------------------   -------------------
 * Bill                  charges
 * Insurance Paid        insurancePaid
 * Allowed               allowedAmount
 * Deduct                deductible
 * Coinsurance           coInsurance
 * CoPay                 copay
 * Patient Resp          patientResponsibility
 * Contract Adj          contractAdjustment
 * Other Adj             otherAdjustment
 * Balance               remainingBalance
 * Status                postingStatus
 * Action                posted
 */
export const deriveCptAmounts = (pc: PatientProcedureCode): CptAmounts => ({
  bill: num(pc.charges),
  insurancePaid: num(pc.insurancePaid),
  allowed: num(pc.allowedAmount),
  deduct: num(pc.deductible),
  coIns: num(pc.coInsurance),
  coPay: num(pc.copay),
  patientResp: num(pc.patientResponsibility),
  contractAdj: num(pc.contractAdjustment),
  otherAdj: num(pc.otherAdjustment),
  balance: num(pc.remainingBalance),
});

// ---------------------------------------------------------------------------
// ERA detail page only.
//
// The ERA get API returns the payer's raw figures (charges, insurancePaid,
// adjustment, writeOff, copay, coInsurance, deductible) but not the derived
// ones — allowedAmount, contractAdjustment, otherAdjustment, remainingBalance
// and patientResponsibility come back null until the line is posted. So the ERA
// page computes them, and posting sends what it computed. Every other screen
// reads a claim that has already been posted and maps the stored fields
// directly (deriveCptAmounts above) — none of the maths below applies there.
// ---------------------------------------------------------------------------

// A CPT line's adjustment is Contract when its own `adjustmentType` says so,
// otherwise it follows the claim's adjustment group code: CO (contract
// obligation) -> Contract, every other group (OA, PI, ...) -> Other.
const isContractAdjustment = (
  pc: PatientProcedureCode,
  claimGroupCode?: string
): boolean => {
  const adjType = (pc.adjustmentType ?? "").toLowerCase();
  if (adjType) return adjType === "contract";
  return (claimGroupCode ?? "").toUpperCase().startsWith("CO");
};

export const computeEraCptAmounts = (
  pc: PatientProcedureCode,
  claimGroupCode?: string
): CptAmounts => {
  const bill = Number(pc.charges ?? pc.total ?? 0);
  const adjustment = Number(pc.adjustment ?? 0);
  const writeOff = Number(pc.writeOff ?? 0);
  const insurancePaid = Number(pc.insurancePaid ?? 0);
  const deduct = Number(pc.deductible ?? 0);
  const coIns = Number(pc.coInsurance ?? 0);
  const coPay = Number(pc.copay ?? 0);
  const patientResp =
    Number(pc.patientResponsibility ?? 0) || deduct + coIns + coPay;

  const isContract = isContractAdjustment(pc, claimGroupCode);
  const contractAdj = isContract ? adjustment : 0;
  const otherAdj = isContract ? 0 : adjustment;

  const allowed = bill - adjustment - writeOff;
  const balance = bill - insurancePaid - patientResp - contractAdj - otherAdj;

  return {
    bill: round2(bill),
    insurancePaid: round2(insurancePaid),
    allowed: round2(allowed),
    deduct: round2(deduct),
    coIns: round2(coIns),
    coPay: round2(coPay),
    patientResp: round2(patientResp),
    contractAdj: round2(contractAdj),
    otherAdj: round2(otherAdj),
    balance: round2(balance),
  };
};

/**
 * The CPT line as the ERA post payload expects it: the amounts the ERA page
 * computed and displayed, written into the DTO's fields, plus the line's posted
 * state. Posting is what turns those computed numbers into stored ones, which
 * is why every other screen can then read them directly.
 */
export const buildCptForPost = (
  pc: PatientProcedureCode,
  claimGroupCode: string | undefined,
  posted: boolean
): PatientProcedureCode => {
  const a = computeEraCptAmounts(pc, claimGroupCode);

  return {
    ...pc,
    insurancePaid: a.insurancePaid ?? 0,
    writeOff: round2(Number(pc.writeOff ?? 0)),
    adjustment: round2(Number(pc.adjustment ?? 0)),
    deductible: a.deduct ?? 0,
    coInsurance: a.coIns ?? 0,
    copay: a.coPay ?? 0,
    patientResponsibility: a.patientResp ?? 0,
    allowedAmount: a.allowed ?? 0,
    contractAdjustment: a.contractAdj ?? 0,
    otherAdjustment: a.otherAdj ?? 0,
    remainingBalance: a.balance ?? 0,
    posted,
    // ECRH-5475 — denial fields mapped 1:1 so the backend persists and returns
    // them under the same keys: comments / denied / denialCode.
    comments: pc.comments ?? "",
    denied: Boolean(pc.denied),
    denialCode: pc.denialCode ?? "",
    // A denied line posts with DENIED status so Claim Details reflects it; only
    // non-denied posted lines are PAID.
    postingStatus: (posted
      ? pc.denied
        ? "DENIED"
        : "PAID"
      : "UNPAID") as PatientProcedureCodePostingStatusEnum,
  };
};

/** Claim-level totals for the ERA post payload — sum of the computed lines. */
export const sumEraCptAmounts = (
  pcs: PatientProcedureCode[] | undefined,
  claimGroupCode?: string
): CptAmounts => {
  const rows = (pcs ?? []).map((pc) => computeEraCptAmounts(pc, claimGroupCode));
  const total = (key: keyof CptAmounts): number =>
    round2(rows.reduce((acc, r) => acc + (r[key] ?? 0), 0));

  return {
    bill: total("bill"),
    insurancePaid: total("insurancePaid"),
    allowed: total("allowed"),
    deduct: total("deduct"),
    coIns: total("coIns"),
    coPay: total("coPay"),
    patientResp: total("patientResp"),
    contractAdj: total("contractAdj"),
    otherAdj: total("otherAdj"),
    balance: total("balance"),
  };
};

